import React, { useEffect, useState, useRef, useContext, useMemo } from 'react'
import {
  StyleSheet,
  View,
  Text,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native'
import { FreshWaterContext } from '../../context/FreshWater/FreshWaterContext'
import { OptionsItem } from '../OptionsItem'
import { corporateColor } from '../../theme/colors'
import { Option } from '../../context/FreshWater/types'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SeaWaterContext } from '../../context/SeaWater/SeaWaterContext'
import { useTranslation } from 'react-i18next'
import { InitialInputVoiceSearch } from '../VoiceSearch/InitialInput/InitialInput'
import { NecropsyContext } from '../../context/Necropsy/Provider'
import { DataMapType, DataMapTypeByParent } from '../../types'

interface Props {
  parentId: number
  onChange: (value: number) => void
  initialValue?: number
  getName?: (name: string) => void
}

export const PondCage = ({ parentId, onChange, initialValue, getName }: Props) => {
  const {
    user: { areaSelected },
  } = useContext(AuthContext)

  const { ponds } = useContext(FreshWaterContext)
  const { cages } = useContext(SeaWaterContext)
  const { cages: cagesByCenter } = useContext(NecropsyContext)

  const dataMap: DataMapTypeByParent = useMemo(
    () => ({
      sweetWater: ponds,
      seaWater: cages,
      health: cagesByCenter,
    }),
    [],
  )

  const { t } = useTranslation()
  const [selectedOption, setSelectedOption] = useState<number | null>(initialValue ?? null)
  const [selectedName, setSelectedName] = useState('')
  const isFirstRender = useRef(true)
  const [search, setSearch] = useState('')
  const [filteredData, setfilteredData] = useState<{ name: string; id: number }[]>([])

  const scrollPosition = useRef<number>(0)
  const flatListRef = useRef<FlatList<Option>>(null)

  useEffect(() => {
    onChange(selectedOption ?? 0)
    getName && getName(selectedName)
  }, [selectedOption])

  useEffect(() => {
    const data = dataMap[areaSelected!]
    if (data) {
      setfilteredData(data[parentId] ?? [])
    } else {
      setfilteredData([])
    }
  }, [parentId])

  useEffect(() => {
    flatListRef.current?.scrollToOffset({ offset: scrollPosition.current, animated: false })

    if (!isFirstRender.current) {
      setSelectedOption(null)
    }
    isFirstRender.current = false
  }, [parentId])

  const handleOptionPress = (item: { id: number; name: string }) => {
    setSelectedOption(item.id === selectedOption ? null : item.id)
    setSelectedName(item.id === selectedOption ? '' : item.name)
  }

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y
    if (!isNaN(offsetY)) {
      scrollPosition.current = offsetY
    }
  }
  useEffect(() => {
    setfilteredData((currentData) => {
      return currentData.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
    })
  }, [search])

  return (
    <View style={styles.container}>
      <Text style={styles.title}>
        {t(`Selecciona ${areaSelected === 'sweetWater' ? 'un estanque' : 'una jaula'}`)}
      </Text>
      <InitialInputVoiceSearch
        placeholder={areaSelected === 'sweetWater' ? t('BUSCAR ESTANQUE') : t('BUSCAR JAULA')}
        getSearchValue={(value) => setSearch(value)}
        hasInput
      />
      <FlatList
        ref={flatListRef}
        style={styles.flatList}
        data={filteredData}
        renderItem={({ item }) => (
          <OptionsItem
            item={item}
            onPress={() => handleOptionPress(item)}
            isSelected={item.id === selectedOption}
          />
        )}
        onScroll={handleScroll}
        horizontal
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    marginTop: 20,
  },
  title: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: corporateColor,
  },
  flatList: {
    marginTop: 10,
  },
})
